import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
	faBuilding,
	faChevronDown,
	faPlus,
	faTriangleExclamation,
	faUsers,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { HowScoringWorks } from "../../shell/HowScoringWorks";
import { ScoreLeaveGuard } from "../../shell/ScoreLeaveGuard";
import { ScoreMatrix } from "../../shell/ScoreMatrix/ScoreMatrix";
import { ScoringPickerMenu } from "../../shell/ScoreMatrix/ScoringPickerMenu";
import { ScoreSaveBar } from "../../shell/ScoreSaveBar";
import { ScoreShell } from "../../shell/ScoreShell";
import { PersonaView, SegmentView } from "../../shell/scoreConfigView";
import { scoreStepPath } from "../../shell/scoreSteps";
import { useFocusTarget } from "../../shell/useFocusTarget";
import { saveBlockerText } from "../../shell/saveBlocker";
import { useConfigDraft } from "../../shell/useConfigDraft";
import { useScoreConfig } from "../../shell/useScoreConfig";

import { OverviewSummary } from "./OverviewSummary";
import { computeOverviewStats } from "./utils/overviewStats";

const HELP_URL = "https://help.signalist.io/lead-scoring";

/** The route a cell's "Create new Lead Scoring" opens, carrying the cell to pre-assign. */
export const createScoringHref = (segment: SegmentView, persona: PersonaView): string =>
	`${scoreStepPath("scoring")}?create=1&segment=${encodeURIComponent(segment.key)}&persona=${persona.id}`;

/** The route that opens one lead scoring on the Lead Scoring tab. */
export const editScoringHref = (criteriaId: number): string =>
	`${scoreStepPath("scoring")}?focus=scoring%3A${criteriaId}`;

/**
 * Overview (PREC-4055): which lead scoring applies to which segment × persona
 * combination, edited in the matrix and saved in one go.
 */
export const ScoreOverview: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { score, criteria, refetch } = useScoreConfig();
	const { target, clear: clearFocus } = useFocusTarget();
	const edits = useConfigDraft(score, criteria, refetch, {
		saveError: t("leadScoring.saveError"),
		blocked: (blocker) => saveBlockerText(blocker, t),
	});

	const addButtonRef = useRef<HTMLButtonElement>(null);
	const [addOpen, setAddOpen] = useState(false);

	const stats = useMemo(
		() =>
			computeOverviewStats(
				edits.segments,
				edits.personas,
				criteria,
				edits.isLinked,
				edits.assignmentOf
			),
		[edits.segments, edits.personas, criteria, edits.isLinked, edits.assignmentOf]
	);

	const addMenu = (
		<>
			<button
				ref={addButtonRef}
				className="flex h-6 cursor-pointer items-center gap-1 rounded-md border border-border-light bg-white px-2 text-[11px] font-medium text-text hover:bg-background-light"
				data-testid="overview-add"
				type="button"
				onClick={() => setAddOpen((current) => !current)}
			>
				<Icon className="h-3 w-3" icon={faPlus} />
				{t("leadScoring.overview.add")}
				<Icon className="h-2.5 w-2.5" icon={faChevronDown} />
			</button>
			{addOpen && addButtonRef.current && (
				<ScoringPickerMenu
					anchorEl={addButtonRef.current}
					items={[
						{
							icon: faBuilding,
							label: t("leadScoring.overview.addSegment"),
							onClick: () => navigate(`${scoreStepPath("company")}?add=segment`),
						},
						{
							icon: faUsers,
							label: t("leadScoring.overview.addPersona"),
							onClick: () => navigate(`${scoreStepPath("prospect")}?add=persona`),
						},
					]}
					onClose={() => setAddOpen(false)}
				/>
			)}
		</>
	);

	return (
		<ScoreShell helpUrl={HELP_URL} intro={t("leadScoring.overview.intro")} step="overview">
			{stats.missing > 0 && (
				<div
					className="mb-3 flex items-start gap-2 rounded-lg border border-status-error-light bg-error-light px-3 py-2 text-xs text-status-error"
					data-testid="overview-missing-banner"
				>
					<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
					<span>
						<b className="font-semibold">
							{t("leadScoring.overview.missingBanner", { count: stats.missing })}
						</b>{" "}
						{t("leadScoring.overview.missingBannerBody")}
					</span>
				</div>
			)}

			<ScoreMatrix
				assignmentOf={edits.assignmentOf}
				corner={addMenu}
				criteria={criteria}
				focusTarget={target}
				isLinked={edits.isLinked}
				personas={edits.personas}
				segments={edits.segments}
				onClear={edits.clear}
				onCreate={(segment, persona) => navigate(createScoringHref(segment, persona))}
				onDeactivate={(segment, persona) => edits.setLinked(segment, persona, false)}
				onEdit={(criteriaId) => navigate(editScoringHref(criteriaId))}
				onFocusDone={clearFocus}
				onPick={edits.pick}
				onRename={edits.rename}
			/>

			<div className="mt-6 flex flex-col gap-3">
				<OverviewSummary
					stats={stats}
					onCreateScoring={() => navigate(`${scoreStepPath("scoring")}?create=1`)}
					onEditScoring={(criteriaId) => navigate(editScoringHref(criteriaId))}
				/>
				<HowScoringWorks />
			</div>

			<ScoreSaveBar
				dirty={edits.dirty}
				error={edits.error}
				saving={edits.saving}
				onDiscard={edits.discard}
				onSave={edits.save}
			/>
			<ScoreLeaveGuard when={edits.dirty} onDiscard={edits.discard} onSave={edits.save} />
		</ScoreShell>
	);
};
